import { ContractExcelColumnsService } from '../../service/data/contract-excel-columns.service';
import { Component, ChangeDetectionStrategy } from '@angular/core';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { CommandFactoryService } from '../../../core/service/factory/command-factory.service';
import { MessageService } from '../../../core/service/message.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { leaseAgreementCommand } from '../../command/lease-agreement.command';
import { ActivatedRoute } from '@angular/router';
import { ParkingContract } from '../../model/parking-contract';
import { ContractService } from '../../service/base/contract';
import { ApproveContractCommandHandler } from '../../service/command/approve-contact-command-handler.service';
import { ContractCommandHandlerResolver } from '../../service/command/contract-command-handler-resolver.service';
import { ParkingContractCommandFactory } from '../../service/command/factory/parking-contract-command-factory.service';
import { PrintContractCommandHandler } from '../../service/command/print-contract-command-handler.service';
import { PrintParkingContractFormCommandHandler } from '../../service/command/print-parkingcontract-command-handler';
import { ShowLeaseAgreementInvoicesCommandHandler } from '../../service/command/show-lease-agreement-invoices-command-handler';
import { TerminateContractCommandHandler } from '../../service/command/terminate-contract-command-handler.service';
import { WithdrawApprovalCommandHandler } from '../../service/command/withdraw-approval-command-handler.service';
import { WithdrawTerminationCommandHandler } from '../../service/command/withdraw-termination-command-handler.service';
import { ColumnDefinitionService } from '../../service/data/column-definition.service';
import { ParkingContractService } from './../../service/data/parking-contract.service';
import { VoidContractCommandHandler } from '../../service/command/void-contract-command-handler.service';
import { ESignLeaseAgreementContractCommandHandler } from '../../service/command/esign-lease-agreement-document-command-handler.service';
import { UserAccountService } from '../../../common/service/user-account.service';
import { Features, ModuleNames } from '../../../common/model/account-definition';
import { ESignKeyRecieptCommandHandler } from '../../service/command/esign-key-reciept-document-command-handler.service';
import { ContractManagerPagedBase } from '../base/contract-manager-paged';
import { CustomPropertyService } from '../../service/data/custom-property.service';
import { CustomPropertyVisibilityType } from '../../model/const/enums';
import { CustomPropertyHelper } from '../../helper/customproperties-helper';
import { PropertyService } from '../../service/data/property.service';
import { CompanyService } from '../../service/data/company.service';
import { ParkingLeaseAgreementListService } from '../../service/data/lease-agreement-list.service';

@Component({
    selector: 'app-parking-contract-manager',
    templateUrl: './../../../core/component/paged-object-manager/paged-object-manager.html',
    styleUrls: ['parking-contract-manager.component.scss', './../../../core/component/object-manager/object-manager.template.scss'],
    providers: [
        ContractExcelColumnsService,
        ApproveContractCommandHandler,
        ContractCommandHandlerResolver,
        ShowLeaseAgreementInvoicesCommandHandler,
        TerminateContractCommandHandler,
        VoidContractCommandHandler,
        ESignLeaseAgreementContractCommandHandler,
        ESignKeyRecieptCommandHandler,
        WithdrawApprovalCommandHandler,
        WithdrawTerminationCommandHandler,
        PrintContractCommandHandler,
        PrintParkingContractFormCommandHandler,
        ParkingLeaseAgreementListService,
        { provide: ContractService, useExisting: ParkingContractService },
        { provide: CommandFactoryService, useClass: ParkingContractCommandFactory },
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ParkingContractManagerComponent extends ContractManagerPagedBase<ParkingContract> {
  constructor(
    activatedRoute: ActivatedRoute,
    columnDefinitionService: ColumnDefinitionService,
    excelColumns: ContractExcelColumnsService,
    dispatcher: CommandDispatcher,
    handlerResolver: ContractCommandHandlerResolver,
    service: ParkingContractService,
    errorHandler: MessageService,
    userAccount: UserAccountService,
    navigationService: NavigationService,
    private readonly _customPropertyService: CustomPropertyService,
    private readonly _ch: CustomPropertyHelper,
    private readonly companyService: CompanyService,
    private readonly propertyService: PropertyService,
    leaseAgreementListService: ParkingLeaseAgreementListService
  ) {
    super(
      service,
      dispatcher,
      handlerResolver,
      errorHandler,
      navigationService,
      userAccount,
      companyService,
      propertyService,
      activatedRoute,
      leaseAgreementListService
    );
    this.columnDefinition = columnDefinitionService.create('ParkingContract');
    this.preferencesKey = 'rent.parking-contract-manager.list';
    this.showExcelExportButton = true;
    this.excelFileName = 'Bilplatskontrakt';
    this.excelContext = 'ParkingContract';
    this.excelAdditionalSheetItems = [
      { binding: 'additionalSheet.leaseAgreementRows', caption: 'EXCEL_EXPORT_SETTINGS.LEASE_AGREEMENT_ROWS', selected: false },
    ];
    this.excelColumnDefinition = [...this.columnDefinition];
    this.AddSubscription(excelColumns.create(this.excelContext, this.excelColumnDefinition).subscribe(columns => {
      // Keep custom columns that may have arrived while the article lists were loading.
      const bindings = new Set(this.excelColumnDefinition.map(column => column.binding));
      this.excelColumnDefinition.push(...columns.filter(column => !bindings.has(column.binding)));
    }));

    this.title = 'LEASE_AGREEMENT.PARKING_LIST_HEADER';

    this.AddSubscription(
      userAccount.current.subscribe({
        next: account => {
          if (account.hasAccess(ModuleNames.Messages, Features.ManageESignatures) && account.hasKeyReceiptWriteAccess()) {
            this.addContextMenuItemDefinition('COMMON.COMMAND.ESIGN_KEY_RECIEPT', leaseAgreementCommand.esignKeyReciept);
          }
          this.addContextMenuItemDefinition('LEASE_AGREEMENT.COMMAND.PRINT_PARKING_FORM', leaseAgreementCommand.printParkingForm);
          if (account.hasLeaseAgreementWriteAccess()) {
            this.addContextMenuItemDefinition('LEASE_AGREEMENT.COMMAND.VOID', leaseAgreementCommand.void);
          }
        },
      })
    );
  }
  override filterInternal(filterState: any): void {
    this._ch.applyCustomPropertyFilters(filterState);
  }

  addFilterDefinitions(definition: any) {
    this._customPropertyService.getList().subscribe(customProperties => {
      super.addFilterDefinitions(definition);

      const customPropertiesFilters = this._ch.generateCustomPropertyFilters(
        customProperties,
        definition,
        CustomPropertyVisibilityType.Company | CustomPropertyVisibilityType.Property
      );
      this._ch.createCustomPropertyColumnDefinition(
        customProperties,
        this.columnDefinition,
        CustomPropertyVisibilityType.Company | CustomPropertyVisibilityType.Property
      );
      this._ch.createCustomPropertyColumnDefinition(
        customProperties,
        this.excelColumnDefinition,
        CustomPropertyVisibilityType.Company | CustomPropertyVisibilityType.Property
      );
      this.filterDefinition = [...this.filterDefinition, ...customPropertiesFilters];
    });
  }
}
